/**
 * Verificación administrativa sobre el contenido del documento (Fase A.2).
 *
 * Trabaja sobre el texto ya extraído del PDF, no sobre el archivo: la extracción va en el backend de
 * documentos, que es donde después se enchufa Document Intelligence para los escaneados.
 *
 * Cubre los casos que se resuelven leyendo un PDF con capa de texto, sin OCR y sin costo: RUT con
 * contraseña, RUT en borrador o trámite, RUT que no corresponde al participante y certificado de
 * existencia no vigente.
 *
 * A diferencia de las reglas de `verificacionAdministrativa`, estas trabajan sobre texto y pueden
 * equivocarse: por eso salen con confianza menor a 1, para que la interfaz las muestre como
 * sugerencia que el profesional debe confirmar.
 */

import { Hallazgo, TipoDocumento } from './verificacionAdministrativa';

/** Textos oficiales de los criterios que se resuelven leyendo el documento. */
export const OBSERVACIONES_LECTURA: { [codigo: string]: string } = {
  g: 'El RUT aportado está en borrador, en trámite, suspendido. Debe subsanar RUT del participante debidamente tramitado. No se acepta documento suspendido, en trámite, borrador ni con contraseña.',
  h: 'El RUT aportado solicita contraseña. Debe subsanar RUT del participante sin contraseña. No se acepta documento suspendido, en trámite, borrador ni con contraseña.',
  i: 'El RUT aportado no corresponde al documento de identificación aportado. Debe subsanar RUT legible a nombre del participante. No se acepta documento suspendido, en trámite, borrador ni con contraseña.',
  q: 'El certificado de existencia y representación legal tiene fecha de expedición del {fecha_expedicion}. Debe subsanar certificado de existencia y representación legal de la entidad con fecha de con fecha de expedición inferior 30 días calendario contados a partir de la fecha de apertura de la convocatoria.',
  // PROPUESTOS: redacción pendiente de aprobación del equipo de la convocatoria.
  s: 'El documento de identificación aportado no corresponde al participante inscrito. Debe subsanar copia del documento de identificación vigente del participante, por ambos lados y legible.',
  t: 'El archivo aportado en {documento} no corresponde al documento solicitado. Debe subsanar el archivo correspondiente a {documento}, legible y completo.',
};

/**
 * Lo que el texto del documento debería contener para ser lo que dice ser. Sirve para detectar el
 * archivo cargado en la casilla equivocada, que hoy pasa como aportado sin que nadie lo note.
 */
const PATRONES_CONTENIDO: { [tipo: string]: RegExp } = {
  rut: /REGISTRO UNICO TRIBUTARIO|\bRUT\b|\bDIAN\b/,
  identificacion: /REPUBLICA DE COLOMBIA|CEDULA DE CIUDADANIA|NUMERO UNICO DE IDENTIFICACION|REGISTRADURIA/,
  certificado_existencia: /EXISTENCIA Y REPRESENTACION|CAMARA DE COMERCIO/,
  soy_cultura: /SOY CULTURA|AGENTES CULTURALES|MINISTERIO DE CULTURA/,
};

/**
 * ¿El archivo es el documento que dice ser? Solo concluye cuando pudo leer el texto: sobre un
 * escaneo no se pronuncia, porque no hay con qué.
 */
export const esElDocumentoEsperado = (tipo: string, lectura: LecturaDocumento | undefined): boolean | null => {
  const patron = PATRONES_CONTENIDO[tipo];
  if (!patron || !tieneTextoUtil(lectura)) return null;
  return patron.test(normalizar(lectura?.texto));
};

/** Marcas que la DIAN imprime en un RUT que no está en firme. */
const MARCAS_RUT_INVALIDO = ['BORRADOR', 'EN TRAMITE', 'SUSPENDIDO'];

/**
 * Debajo de esto se asume que la extracción no sirvió (documento escaneado) y no se concluye nada:
 * ese caso lo resuelve la fase con OCR, no esta.
 */
const MINIMO_TEXTO_UTIL = 100;

/** Días calendario de vigencia del certificado de existencia, contados desde la apertura. */
export const DIAS_VIGENCIA_CERTIFICADO = 30;

/** Confianza de las reglas que dependen de interpretar texto: siempre se confirman con el profesional. */
const CONFIANZA_LECTURA = 0.8;

/** Lo que devuelve la extracción para un documento. */
export type LecturaDocumento = {
  /** El PDF pide contraseña: no se pudo abrir */
  cifrado?: boolean;
  /** Texto extraído de la capa de texto del PDF. Vacío si es un escaneo. */
  texto?: string;
  /**
   * El backend ya evaluó si el texto sirve para concluir algo, midiendo longitud y calidad. Cuando
   * viene en false no se concluye nada: el documento va a la fase con OCR.
   */
  utilizable?: boolean;
};

export const normalizar = (texto: any): string =>
  String(texto ?? '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

/** Números de 6 a 11 dígitos: cédulas y NIT, descartando separadores de miles. */
export const numerosDeDocumento = (texto: any): string[] => {
  const encontrados = String(texto ?? '').match(/\b\d{6,11}\b/g) ?? [];
  return Array.from(new Set(encontrados));
};

export const tieneTextoUtil = (lectura: LecturaDocumento | undefined): boolean => {
  // El backend manda: si dice que no es utilizable, no se interpreta aunque haya texto.
  if (lectura?.utilizable === false) return false;
  return String(lectura?.texto ?? '').trim().length >= MINIMO_TEXTO_UTIL;
};

/**
 * ¿El certificado está vigente? Se exige expedición dentro de los 30 días calendario anteriores a la
 * apertura de la convocatoria. Un certificado expedido después de la apertura también sirve: lo que
 * el lineamiento descarta es el que ya está viejo.
 */
export const certificadoVigente = (
  fechaExpedicion: Date | null,
  fechaApertura: Date | null,
  dias: number = DIAS_VIGENCIA_CERTIFICADO
): boolean => {
  if (!fechaExpedicion || !fechaApertura) return true; // sin datos no se concluye nada
  const limite = new Date(fechaApertura.getTime());
  limite.setDate(limite.getDate() - dias);
  return fechaExpedicion >= limite;
};

/**
 * Interpreta fechas en los formatos que aparecen en los documentos colombianos: d/m/aaaa, d-m-aaaa
 * y aaaa-mm-dd. Devuelve null si no reconoce el formato, para no inventar una fecha.
 */
export const interpretarFecha = (valor: any): Date | null => {
  const texto = String(valor ?? '').trim();

  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(texto);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));

  const latino = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(texto);
  if (latino) return new Date(Number(latino[3]), Number(latino[2]) - 1, Number(latino[1]));

  return null;
};

/**
 * Busca la fecha de expedición de un certificado en su texto.
 *
 * Exige que la fecha venga acompañada de la palabra "expedición" o "expedido": un certificado trae
 * varias fechas (constitución, renovación, matrícula) y tomar cualquiera sería adivinar. Si no
 * encuentra esa señal devuelve null y la regla del caso q no concluye nada.
 */
export const extraerFechaExpedicion = (texto: any): { fecha: Date; texto: string } | null => {
  const limpio = normalizar(texto);
  const señal = /EXPEDI\w*[^0-9]{0,40}(\d{1,2}[/-]\d{1,2}[/-]\d{4}|\d{4}-\d{2}-\d{2})/.exec(limpio);
  if (!señal) return null;

  const fecha = interpretarFecha(señal[1]);
  return fecha ? { fecha, texto: señal[1] } : null;
};

const hallazgoLectura = (
  indice: number,
  codigo: string,
  tipo: TipoDocumento,
  nombre: string,
  observacion: string,
  confianza: number
): Hallazgo => ({
  indice,
  codigo,
  tipo,
  nombre,
  estado: 'subsanar',
  observacion,
  confianza,
});

/**
 * Revisa un RUT a partir de su lectura. El orden importa: un PDF cifrado no se puede leer, así que
 * ese caso manda sobre los demás.
 */
export const analizarRut = (
  lectura: LecturaDocumento | undefined,
  numeroDocumentoParticipante: any
): { codigo: string; confianza: number } | null => {
  if (lectura?.cifrado) return { codigo: 'h', confianza: 1 };
  if (!tieneTextoUtil(lectura)) return null; // escaneado: lo resuelve la fase con OCR

  // Si el archivo ni siquiera es un RUT, no tiene sentido opinar sobre su estado
  if (esElDocumentoEsperado('rut', lectura) === false) {
    return { codigo: 't', confianza: CONFIANZA_LECTURA };
  }

  const texto = normalizar(lectura?.texto);

  if (MARCAS_RUT_INVALIDO.some((marca) => texto.includes(marca))) {
    return { codigo: 'g', confianza: CONFIANZA_LECTURA };
  }

  const documento = String(numeroDocumentoParticipante ?? '').trim();
  if (documento) {
    const numeros = numerosDeDocumento(lectura?.texto);
    if (numeros.length > 0 && !numeros.includes(documento)) {
      return { codigo: 'i', confianza: CONFIANZA_LECTURA };
    }
  }

  return null;
};

/**
 * Revisa la cédula: hoy solo aplica cuando el participante sube la cédula digital en PDF, que trae
 * capa de texto. Las fotos y los escaneos quedan para la fase con OCR.
 */
export const analizarCedula = (
  lectura: LecturaDocumento | undefined,
  numeroDocumentoParticipante: any
): { codigo: string; confianza: number } | null => {
  if (!tieneTextoUtil(lectura)) return null;

  if (esElDocumentoEsperado('identificacion', lectura) === false) {
    return { codigo: 't', confianza: CONFIANZA_LECTURA };
  }

  const documento = String(numeroDocumentoParticipante ?? '').trim();
  if (!documento) return null;

  const numeros = numerosDeDocumento(lectura?.texto);
  if (numeros.length > 0 && !numeros.includes(documento)) {
    return { codigo: 's', confianza: CONFIANZA_LECTURA };
  }

  return null;
};

/** Revisa el certificado de existencia: solo concluye si logró interpretar una fecha. */
export const analizarCertificado = (
  lectura: LecturaDocumento | undefined,
  fechaApertura: Date | null,
  fechaExpedicion: Date | null,
  textoFechaExpedicion: string
): { codigo: string; confianza: number; observacion: string } | null => {
  if (!tieneTextoUtil(lectura) || !fechaExpedicion || !fechaApertura) return null;
  if (certificadoVigente(fechaExpedicion, fechaApertura)) return null;

  return {
    codigo: 'q',
    confianza: CONFIANZA_LECTURA,
    observacion: OBSERVACIONES_LECTURA.q.replace('{fecha_expedicion}', textoFechaExpedicion),
  };
};

export type ContextoLectura = {
  numeroDocumentoParticipante?: any;
  fechaApertura?: Date | null;
  /** Fecha de expedición ya extraída del certificado, cuando la haya */
  expedicionCertificado?: { fecha: Date | null; texto: string };
};

/**
 * Aplica las reglas de lectura sobre los documentos que la fase determinista dejó pendientes.
 *
 * `lecturas` va indexado por la posición del documento dentro de documentos_administrativos, que es
 * la misma que reporta `verificarDocumentosAdministrativos` en `requierenLectura`.
 */
export const verificarConLectura = (
  requierenLectura: { indice: number; nombre: string; tipo: TipoDocumento }[],
  lecturas: { [indice: number]: LecturaDocumento },
  contexto: ContextoLectura = {}
): Hallazgo[] => {
  const hallazgos: Hallazgo[] = [];

  requierenLectura.forEach(({ indice, nombre, tipo }) => {
    const lectura = lecturas[indice];

    const observacionDe = (codigo: string) =>
      OBSERVACIONES_LECTURA[codigo].replace(/\{documento\}/g, nombre);

    if (tipo === 'rut') {
      const resultado = analizarRut(lectura, contexto.numeroDocumentoParticipante);
      if (resultado) {
        hallazgos.push(
          hallazgoLectura(indice, resultado.codigo, tipo, nombre, observacionDe(resultado.codigo), resultado.confianza)
        );
      }
      return;
    }

    if (tipo === 'identificacion') {
      const resultado = analizarCedula(lectura, contexto.numeroDocumentoParticipante);
      if (resultado) {
        hallazgos.push(
          hallazgoLectura(indice, resultado.codigo, tipo, nombre, observacionDe(resultado.codigo), resultado.confianza)
        );
      }
      return;
    }

    if (tipo === 'certificado_existencia') {
      // Si no llega dada desde afuera, se intenta sacar del propio texto del certificado
      const expedicion = contexto.expedicionCertificado ?? extraerFechaExpedicion(lectura?.texto);
      const resultado = analizarCertificado(
        lectura,
        contexto.fechaApertura ?? null,
        expedicion?.fecha ?? null,
        expedicion?.texto ?? ''
      );
      if (resultado) {
        hallazgos.push(hallazgoLectura(indice, resultado.codigo, tipo, nombre, resultado.observacion, resultado.confianza));
      }
    }
  });

  return hallazgos;
};
